import { useMemo, useState } from 'react'
import { useTable, useSortBy } from 'react-table'

import Icon from '@mui/material/Icon'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import AdxInfoModal from '../modal/AdxInfoModal'
import { useMutation, useQueryClient } from 'react-query'
import {
  removeParticipant,
  removeParticipantinEvaluation,
} from 'services/api/modules/performance/appraisal/participants'
import { useLocation } from 'react-router-dom'

const PerfSortTable = ({
  data,
  column,
  nav,
  onClick,
  sx,
  sxHeader,
  sxCell,
  sxNav,
  stickyHeader,
  allColumnSorted = true,
  deleteUser = false,
  fromEvaluation = false,
  getParticipants,
  height,
  ...rest
}) => {
  const queryClient = useQueryClient()
  const { pathname } = useLocation()
  const id = pathname.split('/').pop()
  const [isModalOpen, setModalOpen] = useState(false)
  const [userName, setUserName] = useState('')
  const tableHeader = useMemo(() => column, [column])
  const tableInstance = useTable(
    {
      columns: tableHeader,
      data: data,
      sortTypes: {
        alphanumeric: (prev, curr, columnId) => {
          if (prev?.original[columnId] === null) {
            return -1
          }
          if (curr?.original[columnId] === null) {
            return 1
          }

          if (
            typeof prev?.original[columnId] === 'number' &&
            isFinite(prev?.original[columnId]) &&
            typeof curr?.original[columnId] === 'number' &&
            isFinite(curr?.original[columnId])
          ) {
            if (prev?.original[columnId] > curr?.original[columnId]) {
              return 1
            } else if (prev?.original[columnId] < curr?.original[columnId]) {
              return -1
            } else {
              return 0
            }
          } else {
            if (prev?.original[columnId]?.toLowerCase() > curr?.original[columnId]?.toLowerCase()) {
              return 1
            } else if (
              prev?.original[columnId]?.toLowerCase() < curr?.original[columnId]?.toLowerCase()
            ) {
              return -1
            } else {
              return 0
            }
          }
        },
      },
    },
    useSortBy
  )
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance

  // remove user from an appraisal
  const { mutate: removeUserEvaluation } = useMutation(removeParticipantinEvaluation, {
    onSuccess: async () => {
      await getParticipants('')
      queryClient.invalidateQueries(['appraisalData', parseInt(id)])
      queryClient.resetQueries(['appraisalData', parseInt(id)])
    },
  })

  // remove user from specific evaluation in an appraisal
  const { mutate: removeUser } = useMutation(removeParticipant, {
    onSuccess: async () => {
      await getParticipants('')
      // unable to update participants as it is using uselocation.state
      // queryClient.invalidateQueries(['evaluationData', parseInt(appraisalId)])
      // queryClient.resetQueries(['evaluationData', parseInt(appraisalId)])
    },
  })

  const handleClose = () => {
    setModalOpen(false)
  }

  const handleConfirm = (id, staff_id) => {
    handleClose()
    fromEvaluation
      ? removeUserEvaluation({ appraisalId: id, user_id: staff_id })
      : removeUser({ evaluation_id: id, user_id: staff_id })
  }

  return (
    <>
      <TableContainer
        className="scrollbar"
        sx={{
          height: height,
          boxShadow: '0px 0px 10px lightgray',
          '& th.MuiTableCell-root': {},
          '& td.MuiTableCell-root': {
            padding: '0px',
          },
          ...sx,
        }}
        {...rest}
      >
        <Table stickyHeader={stickyHeader} {...getTableProps}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <TableCell
                    align="center"
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'light-gray-text.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        ...sxHeader,
                      }}
                    >
                      {column.render('Header')}
                      {allColumnSorted && (
                        <>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon sx={{ fontSize: '18px', color: 'alternate.main' }}>
                                arrow_drop_up
                              </Icon>
                            ) : (
                              <Icon sx={{ fontSize: '18px', color: 'alternate.main' }}>
                                arrow_drop_down
                              </Icon>
                            )
                          ) : (
                            <Icon
                              sx={{
                                fontSize: '18px',
                                color: 'gray-inactive-button.main',
                              }}
                            >
                              arrow_drop_down
                            </Icon>
                          )}
                        </>
                      )}
                    </Typography>
                  </TableCell>
                ))}
                {nav && (
                  <TableCell>
                    <Typography></Typography>
                  </TableCell>
                )}
                {deleteUser && (
                  <TableCell>
                    <Typography></Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell, index) => {
                    return (
                      <TableCell align="center" {...cell.getCellProps()}>
                        <Box
                          sx={{
                            textAlign: index === 0 && 'left',
                            p: 2,
                            ...sxCell,
                          }}
                        >
                          {cell.render('Cell')}
                        </Box>
                      </TableCell>
                    )
                  })}
                  {nav && (
                    <TableCell align="right">
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: 'alternate.main',
                          cursor: 'pointer',
                          padding: '18px 30px',
                          ...sxNav,
                        }}
                        onClick={() => onClick(row.original)}
                      >
                        {nav}
                      </Typography>
                    </TableCell>
                  )}
                  {deleteUser && (
                    <TableCell align="right">
                      <DeleteForeverIcon
                        onClick={() => {
                          setModalOpen(!isModalOpen)
                          setUserName(row.original)
                        }}
                        sx={{
                          cursor: 'pointer',
                          color: 'error.main',
                          marginRight: '30px',
                        }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AdxInfoModal
        variant="warning"
        open={isModalOpen}
        onClose={handleClose}
        muiIcon="warning"
        iconColor="error.main"
        title={`Do you want to remove ${userName.name} from this ${
          fromEvaluation ? 'appraisal?' : 'evaluation?'
        }`}
        subtitle="Any unsaved changes will be discarded"
        footerButtons
        firstBtnLabel="Confirm"
        firstBtnHandle={() => handleConfirm(id, userName.staff_id)}
        secondBtnLabel="Close"
        secondBtnHandle={handleClose}
      ></AdxInfoModal>
    </>
  )
}

export default PerfSortTable
